<template>
  <div class="form__group form__group--move form__group--datepicker" :class="getInstanceClass">
    <div class="form__input" :class="isFocused">
      <input
        :id="`datepicker_${id}`"
        :value="displayDate"
        :disabled="disabled"
        :name="name"
        ref="dateInput"
        autocomplete="off"
        @input="onInput"
        @blur="onBlur"
        @focus="onFocus"
        @keydown.enter.prevent="onBlur"
        type="text"/>
      <div class="datepicker" v-if="hasFocus">
        <div class="datepicker__content">
          <div class="datepicker__nav">
            <div class="datepicker__button datepicker__button--prev" @click.stop="shiftValue(-1)">
              <lennoc-icon image="chevron-left" domclass="icon--static" />
            </div>
            <div class="datepicker__label">
              <span
                v-if="pickerView === 'month'"
                @click.stop="pickerView = 'year'"
              >{{ displayValue.monthName }} - </span>
              <span @click.stop="pickerView = 'decades'">{{ displayValue.year }}</span>
            </div>
            <div class="datepicker__button datepicker__button--next" @click.stop="shiftValue(1)">
              <lennoc-icon image="chevron-right" domclass="icon--static" />
            </div>
          </div>
          <div v-if="pickerView === 'month'" class="datepicker__view">
            <div class="datepicker__week">
              <div
                class="datepicker__dayname"
                v-for="dayname in displayValue.dayNames"
                :key="dayname"
              >{{ dayname }}</div>
            </div>
            <div class="datepicker__week" v-for="row in displayValue.weeks" :key="row.id">
              <button
                class="datepicker__day"
                v-for="day in row.days"
                :disabled="day.isDisabled"
                :key="day.id"
                @click.stop="setDate(day.date)"
                :class="dayClass(day)"
              >{{ day.monthDay }}</button>
            </div>
          </div>
          <div v-if="pickerView === 'year'" class="datepicker__view">
            <div class="datepicker__week" v-for="row in monthRows" :key="row.id">
              <button
                class="datepicker__day"
                v-for="cell in row.cells"
                :key="cell.id"
                :disabled="cell.isDisabled"
                @click.stop="setMonth(cell.month)"
              >{{ cell.name }}</button>
            </div>
          </div>
          <div v-if="pickerView === 'decades'" class="datepicker__view">
            <div class="datepicker__week" v-for="row in decades" :key="row.id">
              <button
                class="datepicker__day"
                v-for="cell in row.cells"
                :key="cell.id"
                :disabled="cell.isDisabled"
                @click.stop="setYear(cell.year)"
              >{{ cell.year }}</button>
            </div>
          </div>
          <div class="datepicker__nav">
            <div class="datepicker__button datepicker__button--prev" @click.stop="shiftValue(-1)">
              <lennoc-icon image="chevron-left" domclass="icon--static" />
            </div>
            <div class="datepicker__button" @click.stop="setToday()">Vandaag</div>
            <div class="datepicker__button datepicker__button--next" @click.stop="shiftValue(1)">
              <lennoc-icon image="chevron-right" domclass="icon--static" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <label :for="`datepicker_${id}`">{{ label }}</label>
    <div v-if="hasError" class="error">
      <span>{{ error }}</span>
    </div>
  </div>
</template>

<script>
import day from 'dayjs'
import 'dayjs/locale/nl.js'
import 'dayjs/locale/de.js'
import 'dayjs/locale/en.js'

import customParseFormat from 'dayjs/plugin/customParseFormat'
day.extend(customParseFormat)

export default {
  name: 'LennocDateSelector',
  props: {
    id: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    value: {
      type: String,
      default () {
        return ''
      }
    },
    validators: {
      type: Array,
      default () {
        return []
      },
      validator (value) {
        value.forEach(vld => {
          if (
            !Object.prototype.hasOwnProperty.call(vld, 'validate') ||
            typeof vld.validate !== 'function' ||
            !Object.prototype.hasOwnProperty.call(vld, 'message') ||
            typeof vld.message !== 'string'
          ) {
            return false
          }
        })
        return true
      }
    },
    minimum_date: {
      type: String,
      default () {
        return day().subtract(100, 'years').format('YYYY-MM-DD')
      }
    },
    maximum_date: {
      type: String,
      default () {
        return day().add(100, 'years').format('YYYY-MM-DD')
      }
    },
    required: {
      type: Boolean,
      default () {
        return false
      }
    },
    domclass: {
      type: String,
      default () {
        return ''
      }
    },
    disabled: {
      type: Boolean,
      default () {
        return false
      }
    },
    validationText: {
      type: String,
      default () {
        return ''
      }
    },
    ext_error: {
      type: Boolean,
      default () {
        return false
      }
    },
    name: {
      type: String,
      default () {
        return ''
      }
    },
    presetDateToday: {
      type: Boolean,
      default () {
        return true
      }
    },
    culture: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      displayValue: null,
      currentValue: null,
      pickerView: 'month',
      hasFocus: false,
      inputBuffer: '',
      error: false
    }
  },
  watch: {
    value: function (val) {
      this.displayValue = this.calender(val)
      if (this.value) {
        this.currentValue = this.displayValue
      }
    }
  },
  beforeMount () {
    day.locale(this.culture.toLowerCase())
  },
  mounted () {
    if (this.value) {
      this.displayValue = this.calender(this.value)
      this.currentValue = this.displayValue
    } else {
      this.displayValue = this.calender(this.betweenMinMax)
    }
    document.addEventListener('click', this.handleClickOutside)
  },
  destroyed: function () {
    document.removeEventListener('click', this.handleClickOutside)
  },
  computed: {
    betweenMinMax () {
      if (this.minimum_date && this.maximum_date && !this.presetDateToday) {
        const middle = day(this.maximum_date).valueOf() + (day(this.minimum_date).diff(day(this.maximum_date)) / 2)
        return day(middle)
      }
      return day()
    },
    monthNames () {
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m =>
        day(`2020-${m}-1`).format('MMMM')
      )
    },
    daynames () {
      const days = day().startOf('week').day() === 0
        ? [0, 1, 2, 3, 4, 5, 6]
        : [1, 2, 3, 4, 5, 6, 7]
      return days.map(d =>
        day()
          .day(d)
          .format('ddd')
      )
    },
    hasError () {
      if (this.ext_error) {
        return this.ext_error
      }
      return this.error
    },
    displayDate () {
      return this.currentValue
        ? this.currentValue.date.format('DD-MM-YYYY')
        : ''
    },
    monthRows () {
      const list = []
      for (let r = 0; r < 4; r++) {
        const row = []
        for (let c = 0; c < 3; c++) {
          const index = r * 3 + c
          row.push({
            id: `month_${index}`,
            month: index,
            name: this.monthNames[index]
          })
        }
        list.push({ id: `mrow_${r}`, cells: row })
      }
      return list
    },
    decades () {
      const startYear = 20 * (Math.floor(this.displayValue.year / 20) - 1) + 1
      const list = []
      for (let r = 0; r < 4; r++) {
        const row = []
        for (let c = 0; c < 5; c++) {
          const index = r * 5 + c
          row.push({ id: `year_${index}`, year: startYear + index })
        }
        list.push({ id: `yrow_${r}`, cells: row })
      }
      return list
    },
    getInstanceClass () {
      return [
        this.domclass,
        this.hasError || this.ext_error ? 'form__group--error' : '',
        this.displayDate ? 'form__group--touched' : ''
      ].join(' ')
    },
    isFocused () {
      return this.hasFocus ? 'form__input--focus' : ''
    }
  },
  methods: {
    dateOutsideRange (aDate) {
      const thisDate = day(aDate)
      return (
        thisDate.isBefore(this.minimum_date, 'day') ||
        thisDate.isAfter(this.maximum_date, 'day')
      )
    },
    monthOutsideRange (aMonth) {
      const firstDay = day()
        .year(this.displayValue.date.year())
        .month(aMonth)
        .date(1)
      const lastDay = firstDay.add(1, 'month').subtract(1, 'day')
      return this.dateOutsideRange(firstDay) && this.dateOutsideRange(lastDay)
    },
    yearOutsideRange (aYear) {
      return aYear < this.minimum_date.year() || aYear > this.maximum_date.year()
    },
    updateValue: function () {
      this.currentValue = this.displayValue
      this.$emit('input', this.currentValue.date.format('YYYY-MM-DDTHH:mm:ss'))
    },
    shiftValue (amount) {
      let kind
      switch (this.pickerView) {
        case 'month':
          kind = 'month'
          break
        case 'year':
          kind = 'year'
          break
        case 'decades':
          kind = 'year'
          amount *= 20
          break
      }
      this.displayValue = this.calender(
        this.displayValue.date.add(amount, kind)
      )
    },
    setToday () {
      this.displayValue = this.calender()
      this.updateValue()
      this.hasFocus = false
    },
    setDate (aDate) {
      this.displayValue = this.calender(aDate)
      this.updateValue()
      this.hasFocus = false
    },
    setMonth (month) {
      this.currentValue = null
      this.displayValue = this.calender(this.displayValue.date.month(month))
      this.pickerView = 'month'
    },
    setYear (year) {
      this.currentValue = null
      this.displayValue = this.calender(this.displayValue.date.year(year))
      this.pickerView = 'year'
    },
    dayClass (aDate) {
      return aDate.isToday
        ? 'datepicker__day--selected'
        : aDate.isInCurrentMonth
          ? ''
          : 'datepicker__day--greyed'
    },
    onFocus () {
      this.error = ''
      this.hasFocus = true
    },
    parseDateString (dateStr) {
      if (dateStr) {
        return day(
          dateStr,
          ['D M YY', 'D M YYYY', 'DD MM YY', 'DD MM YYYY', 'D-M-YY', 'D-M-YYYY', 'DD-MM-YY', 'DD-MM-YYYY', 'D/M/YY', 'D/M/YYYY', 'DD/MM/YY', 'DD/MM/YYYY', 'D.M.YY', 'D.M.YYYY', 'DD.MM.YY', 'DD.MM.YYYY', 'D:M:YY', 'D:M:YYYY', 'DD:MM:YY', 'DD:MM:YYYY'],
          this.culture.toLowerCase(),
          true
        )
      }
      return null
    },
    onBlur () {
      if (this.inputBuffer !== '') {
        const dat = this.parseDateString(this.inputBuffer)
        if (dat.isValid()) {
          if (this.dateOutsideRange(dat)) {
            this.error = this.getDateRangeError(dat)
            this.$refs.dateInput.blur()
          } else {
            this.setDate(dat)
          }
        } else {
          this.currentValue = null
          this.error = this.validationText
        }
        this.hasFocus = false
        this.inputBuffer = ''
      }
      this.$emit('blur')
    },
    onInput (event) {
      const input = this.parseDateString(event.target.value)
      if (input === null) {
        this.currentValue = null
        return
      }
      if (input.isValid()) {
        this.inputBuffer = input
      }
      this.inputBuffer = event.target.value
    },
    getDateRangeError (aDate) {
      if (aDate.isBefore(this.minimum_date, 'day')) {
        return `U kunt geen datum invoeren vóór ${day(this.minimum_date).format('D MMMM YYYY')}`
      }
      if (aDate.isAfter(this.maximum_date, 'day')) {
        return `U kunt geen datum invoeren na ${day(this.maximum_date).format('D MMMM YYYY')}`
      }
    },
    handleClickOutside (evt) {
      if (!this.$el.contains(evt.target)) {
        this.hasFocus = false
      }
    },
    createDay (aDate, currentMonth) {
      return {
        id: `day_${aDate.date()}`,
        date: aDate.toDate(),
        monthDay: aDate.date(),
        weekDay: aDate.day(),
        isInCurrentMonth: aDate.month() === currentMonth,
        isToday: aDate.isSame(day(), 'day'),
        isDisabled: this.dateOutsideRange(aDate)
      }
    },
    calender (aDate) {
      const current = aDate || new Date()
      const referenceDate = day(current)
      const referenceMonth = referenceDate.month()
      const firstDayOfMonth = referenceDate.startOf('month')
      const weeks = []
      let currentDate = firstDayOfMonth.startOf('week')
      while (!currentDate.isAfter(referenceDate, 'month')) {
        const week = {
          id: `week_${currentDate.month()}_${currentDate.date()}`,
          days: []
        }
        for (let index = 0; index < 7; index += 1) {
          week.days.push(this.createDay(currentDate, referenceMonth))
          currentDate = currentDate.add(1, 'day')
        }
        weeks.push(week)
      }

      return {
        date: referenceDate,
        monthName: referenceDate.format('MMMM'),
        year: referenceDate.year(),
        dayNames: this.daynames,
        weeks: weeks
      }
    }
  }
}
</script>
